/*
 * Copyright (c) 2025 Bolmate B.V.
 */

import {generateUUID} from '@dvrd/dvr-controls';

import {IdleChangeListener, IdleState} from '../../util/interfaces';

import {useEffect} from 'react';

const events: Array<keyof DocumentEventMap> = ['mousemove', 'mousedown', 'keyup', 'touchstart', 'scroll'];
const IDLE_TIME = 5 * 60 * 1000; // 5 minutes

const stateChangeListeners: Map<string, IdleChangeListener> = new Map();
let currentState = IdleState.ACTIVE;
let stateTimeout: number | undefined = undefined;
let abortController = new AbortController();

export function startIdleObserver() {
    for (const event of events)
        document.documentElement.addEventListener(event, onWake, {passive: true, signal: abortController.signal});
    stateTimeout = window.setTimeout(onIdle, IDLE_TIME);
}

export function stopIdleObserver() {
    abortController.abort();
    abortController = new AbortController();
}

export function addIdleListener(listener: IdleChangeListener): string {
    const id = generateUUID();
    stateChangeListeners.set(id, listener);
    return id;
}

export function removeIdleListener(listenerID: string) {
    stateChangeListeners.delete(listenerID);
}

function onIdle() {
    if (currentState === IdleState.IDLE) return;
    onStateChange(IdleState.IDLE);
}

function onWake() {
    clearTimeout();
    if (currentState !== IdleState.ACTIVE) onStateChange(IdleState.ACTIVE);
    stateTimeout = window.setTimeout(onIdle, IDLE_TIME);
}

function onStateChange(newState: IdleState) {
    currentState = newState;
    for (const listener of stateChangeListeners.values()) listener(currentState);
}

function clearTimeout() {
    window.clearTimeout(stateTimeout);
    stateTimeout = undefined;
}

export function useIdleObserver(onStateChange: IdleChangeListener) {
    useEffect(() => {
        const listenerID = addIdleListener(onStateChange);
        return function () {
            removeIdleListener(listenerID);
        };
    }, [onStateChange]);
}
